import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import MilestoneDropdown from 'talent_search/components/dropdowns/milestone_dropdown';
import StagesDropdown from 'talent_search/components/dropdowns/stages_dropdown';
import RejectionReasonsDropdown from 'talent_search/components/dropdowns/rejection_reasons_dropdown';
import ScorecardPerformanceDropdown from 'talent_search/components/dropdowns/scorecard_performance_dropdown';
import ScorecardAttributesSection from 'talent_matching/talent_rediscovery/components/filters/scorecard_attributes_section'; // eslint-disable-line max-len
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { SCORECARD_ATTRIBUTE_MATCH } from 'talent_search/shared/types';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row');

const REDISCOVERY_PAGE_NAME = 'rediscovery' as const;

const ApplicationHistoryFiltersGroup = () => {
  const { apiClient, milestones, focusAttributes } = useRediscoveryTabContext();
  const { watch, setValue } = useFormContext();
  const [
    milestone,
    stages,
    rejectionReasons,
    scorecardPerformance,
    noStrongNo,
    excludeHired,
    scorecardAttributeFilters,
  ] = watch([
    'milestone',
    'stages',
    'rejection_reasons',
    'scorecard_performance',
    'no_strong_no_scores',
    'exclude_hired',
    'scorecard_attribute_filters',
  ]);

  const hasMilestone = !!milestone;
  const hasStages = Array.isArray(stages) && stages.length > 0;
  const hasRejectionReasons =
    Array.isArray(rejectionReasons) && rejectionReasons.length > 0;
  const hasScorecard = scorecardPerformance !== null;
  const hasNoStrongNo = Boolean(noStrongNo);
  const hasExcludeHired = Boolean(excludeHired);
  const hasAttributeFilters =
    Array.isArray(scorecardAttributeFilters) &&
    scorecardAttributeFilters.length > 0;

  const appliedCount = [
    hasMilestone,
    hasStages,
    hasRejectionReasons,
    hasScorecard,
    hasNoStrongNo,
    hasExcludeHired,
    hasAttributeFilters,
  ].filter(Boolean).length;

  const clear = () => {
    setValue('milestone', null, { shouldDirty: true });
    setValue('stages', [], { shouldDirty: true });
    setValue('rejection_reasons', [], { shouldDirty: true });
    setValue('exclude_by_rejection_reasons', true, { shouldDirty: true });
    setValue('scorecard_performance', null, { shouldDirty: true });
    setValue('no_strong_no_scores', false, { shouldDirty: true });
    setValue('exclude_hired', false, { shouldDirty: true });
    setValue('scorecard_attribute_filters', [], { shouldDirty: true });
    setValue('scorecard_attribute_match', SCORECARD_ATTRIBUTE_MATCH.ANY, {
      shouldDirty: true,
    });
  };

  const buttonLabel = `${t('application_history_filters')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('application_history_filters')}
      width="520px"
    >
      <Stack spacing={3}>
        <MilestoneDropdown
          milestones={milestones}
          pageName={REDISCOVERY_PAGE_NAME}
        />
        <StagesDropdown
          apiClient={apiClient}
          pageName={REDISCOVERY_PAGE_NAME}
        />
        <RejectionReasonsDropdown
          apiClient={apiClient}
          pageName={REDISCOVERY_PAGE_NAME}
        />
        <ScorecardPerformanceDropdown pageName={REDISCOVERY_PAGE_NAME} />
        <ScorecardAttributesSection
          fetchScorecardAttributes={apiClient.fetchScorecardAttributes}
          focusAttributes={focusAttributes}
        />
        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default ApplicationHistoryFiltersGroup;
